// =========================================================
// 📁 WithdrawEuros.js
// Page de retrait des euros (PayPal / IBAN)
// =========================================================

// =======================
// IMPORTS REACT
// =======================
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// =======================
// API & STYLES
// =======================
import API from "../../axiosInstance";
import "../../styles/Retrait.css";

// =========================================================
// COMPONENT
// =========================================================
export default function WithdrawEuros() {
  const navigate = useNavigate();

  // -------------------------------------------------------
  // 💶 Solde euros réel
  // -------------------------------------------------------
  const [euros, setEuros] = useState(0);

  // -------------------------------------------------------
  // 🔘 Mode de retrait
  // -------------------------------------------------------
  const [method, setMethod] = useState(""); // "paypal" | "iban"

  // -------------------------------------------------------
  // 📋 Champs PayPal
  // -------------------------------------------------------
  const [paypalEmail, setPaypalEmail] = useState("");

  // -------------------------------------------------------
  // 📋 Champs IBAN
  // -------------------------------------------------------
  const [iban, setIban] = useState("");
  const [bic, setBic] = useState("");
  const [accountName, setAccountName] = useState("");

  // -------------------------------------------------------
  // ⚖️ Conditions légales
  // -------------------------------------------------------
  const [accepted, setAccepted] = useState(false);

  // -------------------------------------------------------
  // ⏳ États UI
  // -------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // =======================================================
  // 🔌 Chargement du solde euros depuis le backend
  // =======================================================
  useEffect(() => {
    async function fetchEuros() {
      try {
        const res = await API.get("/coins/conversion/status");
        setEuros(Number(res.data.euros));
      } catch (err) {
        console.error("❌ Erreur récupération euros :", err);
        setError("Impossible de récupérer votre solde euros.");
      } finally {
        setLoading(false);
      }
    }

    fetchEuros();
  }, []);

  // =======================================================
  // 📤 Soumission demande de retrait
  // =======================================================
  async function handleSubmit() {
    if (!accepted) {
      setError("Vous devez accepter les conditions de retrait.");
      return;
    }

    if (!method) {
      setError("Veuillez sélectionner un mode de retrait.");
      return;
    }

    if (method === "paypal" && !paypalEmail) {
      setError("Veuillez renseigner votre email PayPal.");
      return;
    }

    if (
      method === "iban" &&
      (!iban || !bic || !accountName)
    ) {
      setError("Veuillez compléter toutes les informations bancaires.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      await API.post("/withdraw/euros", {
        method,
        paypal_email: paypalEmail,
        iban,
        bic,
        account_name: accountName,
      });

      setSuccess(true);
    } catch (err) {
      console.error("❌ Erreur retrait euros :", err);
      setError("Erreur lors de la demande de retrait.");
    } finally {
      setSubmitting(false);
    }
  }

  // =======================================================
  // ⏳ Chargement
  // =======================================================
  if (loading) {
    return (
      <div className="retrait-page">
        <div className="retrait-shell">
          <p className="retrait-subtitle">Chargement du solde euros…</p>
        </div>
      </div>
    );
  }

  // =======================================================
  // ✅ Succès
  // =======================================================
  if (success) {
    return (
      <div className="retrait-page">
        <div className="retrait-shell">
          <h2 className="retrait-title">✅ Demande envoyée</h2>
          <p className="retrait-subtitle">
            Votre demande de retrait a bien été enregistrée.
            <br />
            Le traitement peut prendre jusqu’à 5 jours ouvrés.
          </p>

          <button
            className="retrait-back"
            onClick={() => navigate("/retrait")}
          >
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  // =======================================================
  // 🧾 AFFICHAGE PRINCIPAL
  // =======================================================
  return (
    <div className="retrait-page">
      <div className="retrait-shell">

        {/* HEADER */}
        <div className="retrait-header">
          <div>
            <h2 className="retrait-title">💶 Retrait des euros</h2>
            <p className="retrait-subtitle">
              Retirez vos gains vers PayPal ou votre compte bancaire.
            </p>
          </div>

          <button
            className="retrait-back"
            onClick={() => navigate("/retrait")}
          >
            ← Retour
          </button>
        </div>

        {/* SOLDE */}
        <div className="retrait-panel" style={{ marginBottom: 16 }}>
          <p className="retrait-card-desc">
            💰 Solde disponible : <strong>{euros.toFixed(2)} €</strong>
          </p>
        </div>

        {/* CHOIX MÉTHODE */}
        <div className="retrait-panel" style={{ marginBottom: 16 }}>
          <p className="retrait-card-title">Choisissez un mode de retrait</p>

          <label>
            <input
              type="radio"
              name="method"
              value="paypal"
              checked={method === "paypal"}
              onChange={() => setMethod("paypal")}
            />{" "}
            PayPal
          </label>

          <br />

          <label>
            <input
              type="radio"
              name="method"
              value="iban"
              checked={method === "iban"}
              onChange={() => setMethod("iban")}
            />{" "}
            Virement bancaire (IBAN)
          </label>
        </div>

        {/* FORMULAIRE PAYPAL */}
        {method === "paypal" && (
          <div className="retrait-panel">
            <input
              type="email"
              placeholder="Email PayPal"
              value={paypalEmail}
              onChange={(e) => setPaypalEmail(e.target.value)}
            />
          </div>
        )}

        {/* FORMULAIRE IBAN */}
        {method === "iban" && (
          <div className="retrait-panel">
            <input
              placeholder="Nom du titulaire"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
            />
            <input
              placeholder="IBAN"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
            />
            <input
              placeholder="BIC"
              value={bic}
              onChange={(e) => setBic(e.target.value)}
            />
          </div>
        )}

        {/* CONDITIONS LÉGALES */}
        <div className="retrait-panel">
          <label>
            <input
              type="checkbox"
              checked={accepted}
              onChange={() => setAccepted(!accepted)}
            />{" "}
            J’accepte les conditions de retrait et la réglementation européenne.
          </label>
        </div>

        {/* ERREUR */}
        {error && (
          <p className="retrait-subtitle" style={{ color: "salmon" }}>
            {error}
          </p>
        )}

        {/* BOUTON */}
        <button
          className="retrait-back"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Envoi en cours…" : "Demander le retrait"}
        </button>

      </div>
    </div>
  );
}